/**
 * Sync Status Banner — shows channel logo, connection name, sync freshness,
 * status pill, contextual action button, and audit/rejection banners.
 *
 * TikTok-specific: action buttons (activate/deactivate), audit banners,
 * read-only explanation banners for frozen/platform-deactivated products.
 */

import { useState } from "react";
import { ChannelLogo } from "@/features/channels/components/ChannelLogo";
import { formatSyncTime } from "@/features/channels/components/ListingStatusPill";
import { ListingStatusPill } from "@/features/channels/components/ListingStatusPill";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig, parseJsonField } from "./shared";
import { useTikTokActivate, useTikTokDeactivate } from "../hooks/useTikTokLifecycle";

function getSyncFreshness(lastSynced: string | undefined) {
  if (!lastSynced) return { dot: "bg-red-500", textClass: "text-red-600", label: "Never synced" };
  const diff = Date.now() - new Date(lastSynced).getTime();
  const hours = diff / (1000 * 60 * 60);
  if (hours < 1) return { dot: "bg-green-500", textClass: "text-muted-foreground", label: null };
  if (hours < 24) return { dot: null, textClass: "text-muted-foreground", label: null };
  return { dot: "bg-amber-500", textClass: "text-amber-600", label: null };
}

export function MarketplaceSyncStatusWidget({ section, formData }: ExperienceWidgetProps) {
  const channel = section.description || "TikTok Shop";
  const config = getChannelConfig(channel);
  const lastSynced = formData?.[config.lastSynced] as string | undefined;
  const connectionName = formData?.[config.connection] as string | undefined;
  const status = formData?.[config.status] as string | undefined;
  const sync = lastSynced ? formatSyncTime(lastSynced) : null;
  const freshness = getSyncFreshness(lastSynced);
  const isTikTok = channel === "TikTok Shop";

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Sync Status
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        <div className="flex items-center gap-4 px-4 py-5">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <ChannelLogo channel={channel} size={24} className="shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {freshness.dot && (
                  <span className={`inline-block w-2 h-2 rounded-full ${freshness.dot}`} />
                )}
                <span className={`text-sm ${freshness.textClass}`}>
                  {freshness.label || (sync ? `Synced ${sync.relative}` : "Never synced")}
                </span>
              </div>
              {connectionName && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">{connectionName}</p>
              )}
            </div>
          </div>
          {status && <ListingStatusPill status={status} />}
        </div>

        {/* TikTok-specific: banners + action button */}
        {isTikTok && formData && (
          <TikTokStatusActions formData={formData} />
        )}
      </div>
    </div>
  );
}

// ── Status → action mapping ───────────────────────────────────────

function getStatusAction(status: string): { label: string; variant: "default" | "outline"; action: string } | null {
  switch (status) {
    case "activate": return { label: "ปิดการขาย", variant: "outline", action: "deactivate" };
    case "seller_deactivated": return { label: "เปิดการขายอีกครั้ง", variant: "default", action: "activate" };
    case "draft": return { label: "เผยแพร่", variant: "default", action: "activate" };
    case "failed": return { label: "ลองอีกครั้ง", variant: "default", action: "activate" };
    default: return null; // FREEZE, PLATFORM_DEACTIVATED, DELETED — no action
  }
}

// ── TikTok-specific inner component (uses hooks) ──────────────────

function TikTokStatusActions({ formData }: { formData: Record<string, any> }) {
  const status = (formData?.status as string || "").toLowerCase();
  const auditStatus = (formData?.audit_status as string || "").toLowerCase();
  const auditReasons = formData?.audit_reject_reasons as string | undefined;

  const activate = useTikTokActivate();
  const deactivate = useTikTokDeactivate();
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const action = getStatusAction(status);

  const handleAction = () => {
    if (!action) return;
    if (action.action === "deactivate") {
      setConfirmDeactivate(true);
    } else {
      // activate / publish / retry
      activate.mutate(undefined, {
        onSuccess() { toast.success("สินค้าถูกเปิดการขายแล้ว"); },
        onError(err) { toast.error(err?.message || "ดำเนินการไม่สำเร็จ"); },
      });
    }
  };

  const handleConfirmDeactivate = () => {
    deactivate.mutate(undefined, {
      onSuccess() {
        setConfirmDeactivate(false);
        toast.success("สินค้าถูกปิดการขายแล้ว");
      },
      onError(err) { toast.error(err?.message || "ดำเนินการไม่สำเร็จ"); },
    });
  };

  const isPending = activate.isPending || deactivate.isPending;

  return (
    <>
      {/* Audit banners */}
      {auditStatus === "pending" && <AuditPendingBanner />}
      {auditStatus === "rejected" && <AuditRejectedBanner reasons={auditReasons} />}

      {/* Read-only explanation */}
      {status === "freeze" && <ReadOnlyBanner message="สินค้านี้ถูกระงับโดย TikTok" />}
      {status === "platform_deactivated" && <ReadOnlyBanner message="สินค้าถูกปิดการขายโดย TikTok" />}

      {/* Action button */}
      {action && (
        <div className="px-4 pb-4 pt-1">
          <Button
            onClick={handleAction}
            variant={action.variant}
            size="sm"
            className="w-full"
            disabled={isPending}
          >
            {isPending ? "กำลังดำเนินการ..." : action.label}
          </Button>
        </div>
      )}

      {/* Deactivate confirmation dialog */}
      <Dialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ปิดการขายสินค้านี้?</DialogTitle>
            <DialogDescription>
              ลูกค้าจะไม่เห็นสินค้านี้จนกว่าจะเปิดอีกครั้ง
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeactivate(false)}>ยกเลิก</Button>
            <Button onClick={handleConfirmDeactivate} disabled={deactivate.isPending}>
              {deactivate.isPending ? "กำลังดำเนินการ..." : "ปิดการขาย"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Banner components ─────────────────────────────────────────────

function AuditPendingBanner() {
  return (
    <div className="mx-4 mb-2 rounded bg-amber-50 border border-amber-200 px-3 py-2">
      <p className="text-sm text-amber-800">
        กำลังตรวจสอบ — การแก้ไขจะเผยแพร่เมื่อ TikTok อนุมัติ
      </p>
    </div>
  );
}

function AuditRejectedBanner({ reasons }: { reasons?: string }) {
  const parsed = reasons ? parseJsonField<unknown[]>(reasons) : null;
  return (
    <div className="mx-4 mb-2 rounded bg-red-50 border border-red-200 px-3 py-2">
      <p className="text-sm font-medium text-red-800">ถูกปฏิเสธโดย TikTok</p>
      {parsed && Array.isArray(parsed) && parsed.map((r: any, i: number) => (
        <p key={i} className="text-xs text-red-700 mt-1">• {r.message || r}</p>
      ))}
    </div>
  );
}

function ReadOnlyBanner({ message }: { message: string }) {
  return (
    <div className="mx-4 mb-2 rounded bg-gray-100 border border-gray-200 px-3 py-2">
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );
}
